/**
 * Reviews Headless SDK Bridge
 *
 * Owned and maintained by wbtheme. Defines the storefront review Vue
 * components on top of the ReviewsSDK composables: collection card stars,
 * product top stars, the reviews tab, reviews page totals, and the product
 * page review section (histogram, sort, load more, upvote, translations, and
 * the write / report sheets).
 *
 * Markup: snippets/vue-templates.liquid (vue-template-reviews-*).
 * Styles: assets/product-reviews.css.
 * The SDK (product-reviews-headless.js) is built and served by reviews-server
 * and loaded from {reviewsApiUrl}/client/ in snippets/theme-product-reviews.liquid.
 *
 * Dependencies (loaded from CDN before this script):
 *   - Vue 2.7+  → window.Vue
 *   - VueI18n   → window.VueI18n
 *   - axios     → window.axios
 *   - ReviewsSDK → window.ReviewsSDK (product-reviews-headless.js)
 */
(function () {
  'use strict';

  var SDK = window.ReviewsSDK;
  if (!SDK) {
    console.warn('[reviews-bridge] ReviewsSDK not loaded');
    return;
  }

  // ---------------------------------------------------------------------------
  // i18n messages (subset needed for display components)
  // ---------------------------------------------------------------------------
  var messages = {
    en: {
      tabTitle: 'Reviews',
      totalReviewsSeeAll: 'See all reviews',
      totalReviews: '{total} Reviews',
      avgOutOf: '{avg} out of {total} stars',
      customerSatisfaction: 'Customer satisfaction {avg}/{total}',
      emptyTitle: 'Be the first to review this product',
      emptyText: 'Share your experience and help others choose.',
      emptyHiddenTitle: 'Share your experience with this product',
      writeReview: 'Write a review',
      reportTitle: 'Report review',
      reportSuccessTitle: 'Thank you for your report',
      loadMore: 'load more',
      viewingReviews: 'viewing {from}-{to} of {total} reviews',
      onDate: ' on {date}',
      peopleFoundThatHelpful: '{pht} people found this review helpful!',
      feedback: 'Thank you for your feedback',
      translated: 'Original language: {original}, translated by {translator}',
      languages: { all: 'All', en: 'English', de: 'German', fr: 'French', es: 'Spanish' },
      actions: {
        upvote: 'Useful',
        reportAbuse: 'Report',
        submit: 'Submit',
        close: 'Close',
        showOriginal: 'Show original',
        showTranslation: 'Show translation',
      },
      forms: {
        rating: 'Rating',
        ratingHint: 'Tap stars to rate',
        ratingHintDesktop: 'Roll over the stars, then click to rate.',
        title: 'Title of your review',
        titlePlaceholder: 'Write the title of your review here.',
        body: 'Your review',
        bodyPlaceholder: 'Share your honest experience, and help others make better choices.',
        titleMore: 'Write a title',
        bodyMore: 'Write more',
        name: 'First name',
        email: 'Email',
        emailPlaceholder: 'your-email@domain.com',
        emailHint: "Your email won't be displayed publicly.",
        orderName: 'Order number (optional)',
        orderNamePlaceholder: "Order number (don't forget the hash #)",
        reason: 'Reason',
        reasonOptions: {
          inappropriateContent: 'Contains hateful, violent, or inappropriate content',
          spamAdvertising: 'Spam / Advertising',
          offTopic: 'Off-topic',
          conflictOfInterest: 'Conflict of interest'
        },
        reasonBody: 'Details',
        reasonBodyPlaceholder: "What's wrong with this review?",
        addPhotos: 'Add photos',
        heightCmPlaceholder: 'e.g. 175',
        heightFtPlaceholder: 'ft',
        heightInPlaceholder: 'in'
      },
      write: { successTitle: 'Thank you for your review', successSent: 'We\'ve sent a verification link to {email}. Your review goes live once you\'ve confirmed it.', successSpam: 'Can\'t find it? Check your spam folder.' },
      reportMessage: "Thanks. We've received your report. If we find this review to be in violation of our guidelines, we will remove it.",
      sort: { label: 'Sort by', relevance: 'Most relevant', rating: 'Highest rated', newest: 'Newest' },
      errors: { validation: 'Please check your details and try again.', down: 'Reviews are unavailable right now. Please try again later.', critical: 'Something went wrong. Please try again.', rateLimit: 'Too many requests. Please wait a moment and try again.' },
      wizard: { next: 'Next', back: 'Back' }
    },
    'de-DE': {
      tabTitle: 'Bewertungen',
      totalReviewsSeeAll: 'Alle Bewertungen ansehen',
      totalReviews: '{total} Bewertungen',
      avgOutOf: '{avg} von {total} Sternen',
      customerSatisfaction: 'Kundenzufriedenheit {avg}/{total}',
      emptyTitle: 'Sei die Erste, die dieses Produkt bewertet',
      emptyText: 'Teile deine Erfahrung und hilf anderen bei der Wahl.',
      emptyHiddenTitle: 'Teile deine Erfahrung mit diesem Produkt',
      writeReview: 'Eine Bewertung schreiben',
      reportTitle: 'Bewertung melden',
      reportSuccessTitle: 'Danke für deine Meldung',
      loadMore: 'mehr laden',
      viewingReviews: 'Angezeigt {from}-{to} von {total} Bewertungen',
      onDate: ' am {date}',
      peopleFoundThatHelpful: '{pht} Personen fanden diese Bewertung hilfreich!',
      feedback: 'Vielen Dank für dein Feedback',
      translated: 'Ursprüngliche Sprache: {original}, übersetzt von {translator}',
      languages: { all: 'Alle', en: 'Englisch', de: 'Deutsch', fr: 'Französisch', es: 'Spanisch' },
      actions: { upvote: 'Nützlich', reportAbuse: 'Melden', submit: 'Abschicken', close: 'Schließen', showOriginal: 'Original anzeigen', showTranslation: 'Übersetzung anzeigen' },
      forms: {
        rating: 'Bewertung', ratingHint: 'Sterne zum Bewerten antippen', ratingHintDesktop: 'Scrolle über die Sterne und bestätige durch Klick.',
        title: 'Titel deiner Bewertung', titlePlaceholder: 'Schreibe hier den Titel deiner Bewertung.',
        body: 'Deine Bewertung', bodyPlaceholder: 'Teile deine ehrliche Erfahrung und hilf anderen bei ihrer Entscheidung.',
        titleMore: 'Titel schreiben', bodyMore: 'Mehr schreiben',
        name: 'Vorname', email: 'E-Mail', emailPlaceholder: 'deine-email@domain.com', emailHint: 'Deine E-Mail-Adresse wird nicht veröffentlicht.',
        orderName: 'Bestellnummer (optional)', orderNamePlaceholder: 'Bestellnummer (vergiss die Raute # nicht)',
        reason: 'Grund',
        reasonOptions: { inappropriateContent: 'Enthält hasserfüllte, gewalttätige oder unangemessene Inhalte', spamAdvertising: 'Spam / Werbung', offTopic: 'Off-topic / Nicht passend', conflictOfInterest: 'Interessenkonflikt' },
        reasonBody: 'Details', reasonBodyPlaceholder: 'Was ist falsch an dieser Bewertung?', addPhotos: 'Fotos hinzufügen',
        heightCmPlaceholder: 'z.B. 175',
        heightFtPlaceholder: 'ft',
        heightInPlaceholder: 'in'
      },
      write: { successTitle: 'Danke für deine Bewertung', successSent: 'Wir haben einen Bestätigungslink an {email} gesendet. Deine Bewertung wird veröffentlicht, sobald du sie bestätigt hast.', successSpam: 'Nichts erhalten? Schau in deinem Spam-Ordner nach.' },
      sort: { label: 'Sortieren nach', relevance: 'Relevanteste', rating: 'Beste Bewertung', newest: 'Neueste' },
      reportMessage: 'Vielen Dank. Wir haben deine Meldung erhalten. Sollte die Bewertung gegen unsere Richtlinien verstoßen, werden wir sie entfernen.',
      errors: { validation: 'Bitte überprüfe deine Angaben und versuche es erneut.', down: 'Bewertungen sind gerade nicht verfügbar. Bitte versuche es später erneut.', critical: 'Etwas ist schiefgelaufen. Bitte versuche es erneut.', rateLimit: 'Zu viele Anfragen. Bitte warte einen Moment und versuche es erneut.' },
      wizard: { next: 'Weiter', back: 'Zurück' }
    },
    'fr-FR': {
      tabTitle: 'Avis',
      totalReviewsSeeAll: 'Voir tous les avis',
      totalReviews: '{total} Avis',
      avgOutOf: '{avg} sur {total} étoiles',
      customerSatisfaction: 'Satisfaction des clients {avg}/{total}',
      emptyTitle: 'Sois la première à donner ton avis sur ce produit',
      emptyText: 'Partage ton expérience et aide les autres à choisir.',
      emptyHiddenTitle: 'Partage ton expérience avec ce produit',
      writeReview: 'Donne ton avis',
      reportTitle: "Signaler l'avis",
      reportSuccessTitle: 'Merci pour ton signalement',
      loadMore: 'Voir plus',
      viewingReviews: 'Affichage de {from}-{to} sur {total} avis',
      onDate: ' le {date}',
      peopleFoundThatHelpful: '{pht} personnes ont trouvé cela utile!',
      feedback: 'Merci pour ton avis',
      translated: 'Langue originale : {original}, traduit par {translator}',
      languages: { all: 'Tous', en: 'Anglais', de: 'Allemand', fr: 'Français', es: 'Espagnol' },
      actions: { upvote: 'Utile', reportAbuse: 'Signaler', submit: 'Envoyer', close: 'Fermer', showOriginal: "Voir l'original", showTranslation: 'Voir la traduction' },
      forms: {
        rating: 'Note', ratingHint: 'Clique sur les étoiles pour donner ta note', ratingHintDesktop: 'Fais glisser la souris sur les étoiles, puis clique pour donner ta note.',
        title: "Titre de l'avis", titlePlaceholder: 'Écris le titre de ton avis ici.',
        body: 'Ton avis', bodyPlaceholder: 'Partage ton expérience et aide les autres à choisir des produits adaptés à leurs besoins.',
        titleMore: 'Écrire un titre', bodyMore: 'Écrire plus',
        name: 'Prénom', email: 'Adresse e-mail', emailPlaceholder: 'ton-e-mail@domain.com', emailHint: 'Ton adresse e-mail ne sera pas publiée.',
        orderName: 'Numéro de commande (optionnel)', orderNamePlaceholder: "Numéro de commande (n'oublie pas le #)",
        reason: 'Raison',
        reasonOptions: { inappropriateContent: 'Contenu haineux, violent ou inapproprié', spamAdvertising: 'Spam / Publicité', offTopic: 'Hors sujet', conflictOfInterest: "Conflit d'intérêts" },
        reasonBody: 'Détails', reasonBodyPlaceholder: "Quel est le problème avec cet avis ?", addPhotos: 'Ajouter des photos',
        heightCmPlaceholder: 'p.ex. 175',
        heightFtPlaceholder: 'pi',
        heightInPlaceholder: 'po'
      },
      write: { successTitle: "Merci pour ton avis", successSent: "Nous avons envoyé un lien de vérification à {email}. Ton avis sera publié dès que tu l'auras confirmé.", successSpam: "Tu ne le trouves pas ? Vérifie ton dossier spam." },
      sort: { label: "Trier par", relevance: "Plus pertinents", rating: "Mieux notés", newest: "Plus récents" },
      reportMessage: "Merci. Nous avons reçu ton signalement. Si l'avis se trouve en violation de nos directives, nous le supprimerons.",
      errors: { validation: "Merci de vérifier tes informations et de réessayer.", down: "Les avis sont indisponibles pour le moment. Réessaye plus tard.", critical: "Une erreur s'est produite. Merci de réessayer.", rateLimit: "Trop de tentatives. Patiente un instant et réessaye." },
      wizard: { next: 'Suivant', back: 'Retour' }
    },
    'es-ES': {
      tabTitle: 'Valoraciones',
      totalReviewsSeeAll: 'Ver todas las valoraciones',
      totalReviews: '{total} valoraciones',
      avgOutOf: '{avg} de {total} estrellas',
      customerSatisfaction: 'Satisfacción de las clientas {avg}/{total}',
      emptyTitle: 'Sé la primera en valorar este producto',
      emptyText: 'Comparte tu experiencia y ayuda a otras a elegir.',
      emptyHiddenTitle: 'Comparte tu experiencia con este producto',
      writeReview: 'Escribir una valoración',
      reportTitle: 'Denunciar valoración',
      reportSuccessTitle: 'Gracias por avisarnos',
      loadMore: 'cargar más',
      viewingReviews: 'mostrando {from}-{to} de {total} valoraciones',
      onDate: ' el {date}',
      peopleFoundThatHelpful: '¡A {pht} personas les ha resultado útil esta valoración!',
      feedback: 'Gracias por tu opinión',
      translated: 'Idioma original: {original}, traducido por {translator}',
      languages: { all: 'Todos', en: 'Inglés', de: 'Alemán', fr: 'Francés', es: 'Español' },
      actions: { upvote: 'Útil', reportAbuse: 'Denunciar', submit: 'Enviar', close: 'Cerrar', showOriginal: 'Ver original', showTranslation: 'Ver traducción' },
      forms: {
        rating: 'Puntuación', ratingHint: 'Toca las estrellas para valorar', ratingHintDesktop: 'Pasa el ratón por las estrellas y haz clic para valorar.',
        title: 'Título de tu valoración', titlePlaceholder: 'Escribe aquí el título de tu valoración.',
        body: 'Tu valoración', bodyPlaceholder: 'Comparte tu experiencia sincera y ayuda a otras a elegir mejor.',
        titleMore: 'Escribir un título', bodyMore: 'Escribir más',
        name: 'Nombre', email: 'Correo electrónico', emailPlaceholder: 'tu-email@domain.com', emailHint: 'Tu correo electrónico no se mostrará públicamente.',
        orderName: 'Número de pedido (opcional)', orderNamePlaceholder: 'Número de pedido (no olvides la almohadilla #)',
        reason: 'Motivo',
        reasonOptions: { inappropriateContent: 'Contiene contenido de odio, violento o inapropiado', spamAdvertising: 'Spam / publicidad', offTopic: 'No tiene relación con el producto', conflictOfInterest: 'Conflicto de intereses' },
        reasonBody: 'Detalles', reasonBodyPlaceholder: '¿Qué problema hay con esta valoración?',
        addPhotos: 'Añadir fotos',
        heightCmPlaceholder: 'p. ej. 175', heightFtPlaceholder: 'ft', heightInPlaceholder: 'in'
      },
      write: { successTitle: 'Gracias por tu valoración', successSent: 'Hemos enviado un enlace de verificación a {email}. Tu valoración se publicará cuando la hayas confirmado.', successSpam: '¿No lo encuentras? Revisa tu carpeta de spam.' },
      reportMessage: 'Gracias. Hemos recibido tu aviso. Si esta valoración incumple nuestras directrices, la eliminaremos.',
      sort: { label: 'Ordenar por', relevance: 'Más relevantes', rating: 'Mejor valoradas', newest: 'Más recientes' },
      errors: { validation: 'Revisa tus datos e inténtalo de nuevo.', down: 'Las valoraciones no están disponibles en este momento. Inténtalo más tarde.', critical: 'Algo ha salido mal. Inténtalo de nuevo.', rateLimit: 'Demasiadas solicitudes. Espera un momento e inténtalo de nuevo.' },
      wizard: { next: 'Siguiente', back: 'Atrás' }
    }
  };

  var dateTimeFormats = {
    'en-GB': { short: { year: 'numeric', month: '2-digit', day: '2-digit' } },
    'en-US': { short: { year: 'numeric', month: '2-digit', day: '2-digit' } },
    'en-CA': { short: { year: 'numeric', month: '2-digit', day: '2-digit' } },
    'en-AU': { short: { year: 'numeric', month: '2-digit', day: '2-digit' } },
    'de-DE': { short: { year: 'numeric', month: '2-digit', day: '2-digit' } },
    'fr-FR': { short: { year: 'numeric', month: '2-digit', day: '2-digit' } },
    'es-ES': { short: { year: 'numeric', month: '2-digit', day: '2-digit' } }
  };

  // ---------------------------------------------------------------------------
  // Shared state
  // ---------------------------------------------------------------------------
  var client = null;
  var apiBaseUrl = null;
  // 'cm' or 'ft', set per store by the theme (see theme-product-reviews.liquid).
  var heightUnitSystem = 'cm';
  var collectionRatings = null;
  var productOverviews = {};
  var i18nInstance = null;
  var _collectionLoadPromise = null;

  function loadCollectionRatings () {
    if (collectionRatings && collectionRatings.isLoaded && collectionRatings.isLoaded.value) return Promise.resolve();
    if (_collectionLoadPromise) return _collectionLoadPromise;
    _collectionLoadPromise = collectionRatings.load().finally(function () {
      _collectionLoadPromise = null;
    });
    return _collectionLoadPromise;
  }

  function getProductOverview (handle) {
    if (!productOverviews[handle]) {
      productOverviews[handle] = SDK.useProductOverview(client);
    }
    productOverviews[handle].load(handle);
    return productOverviews[handle];
  }

  // True only when the product really has no reviews yet. The API hides the
  // summary below the display threshold or when reviews are switched off, and
  // then sends rating_count null and rating_avg 0, so "no stars" does not mean
  // "no reviews". Prefer the explicit flags; an SDK without them still keeps
  // null (hidden) apart from 0 (none).
  function hasNoReviewsYet (overview) {
    if (!overview.isLoaded.value) return false;
    if (overview.reviewsMeetThreshold && overview.reviewsMeetThreshold.value === false) return false;
    if (overview.reviewsVisible && overview.reviewsVisible.value === false) return false;
    return overview.ratingCount.value === 0;
  }

  var productReviewLists = {};
  function getProductReviewList (handle) {
    if (!productReviewLists[handle]) {
      productReviewLists[handle] = SDK.useReviewList(client);
    }
    return productReviewLists[handle];
  }

  function ensureI18n (locale) {
    if (i18nInstance) return i18nInstance;
    Vue.use(VueI18n);
    i18nInstance = new VueI18n({
      locale: locale || 'en-GB',
      fallbackLocale: 'en',
      silentFallbackWarn: true,
      messages: messages,
      dateTimeFormats: dateTimeFormats
    });
    return i18nInstance;
  }

  // ---------------------------------------------------------------------------
  // Structured data helpers
  // ---------------------------------------------------------------------------
  function findSchemaElement (type) {
    var re = new RegExp('"@type":\\s?"' + type + '"');
    return Array.from(document.querySelectorAll('script[type="application/ld+json"]'))
      .find(function (el) { return re.test(el.textContent); });
  }

  function updateSchema (type, fn) {
    var el = findSchemaElement(type);
    if (!el) return;
    var old = JSON.parse(el.textContent);
    var patch = fn(old);
    if (!patch) return;
    var merged = Object.assign({}, old, patch);
    var json = JSON.stringify(merged);
    if (json !== el.textContent) {
      el.textContent = json;
      document.head.appendChild(el);
    }
  }

  function updateProductSchema (data) {
    updateSchema('Product', function (old) {
      if (data.ratingValue > 0) {
        return {
          aggregateRating: {
            '@type': 'AggregateRating',
            itemReviewed: (old.name || '').toString(),
            ratingCount: data.ratingCount.toString(),
            reviewCount: data.ratingCount.toString(),
            ratingValue: data.ratingValue.toString(),
            bestRating: '5',
            worstRating: '1'
          }
        };
      }
      if (old.aggregateRating) {
        delete old.aggregateRating;
        return old;
      }
    });
  }

  function updateOrganizationSchema (data) {
    updateSchema('Organization', function () {
      var item = "Women's Best";
      var h1 = document.querySelector('h1');
      if (h1) item = h1.textContent.trim();
      return {
        aggregateRating: {
          '@type': 'AggregateRating',
          itemReviewed: item,
          ratingCount: data.ratingCount.toString(),
          reviewCount: data.ratingCount.toString(),
          ratingValue: data.ratingValue.toString(),
          bestRating: '5',
          worstRating: '1'
        }
      };
    });
  }

  // ---------------------------------------------------------------------------
  // Star rating component (pure presentational)
  // ---------------------------------------------------------------------------
  var ProductReviewsStars = {
    name: 'ProductReviewsStars',
    model: { prop: 'rating', event: 'input' },
    props: {
      rating: { type: [Number, String], required: true },
      max: { type: Number, default: 5 },
      interactive: { type: Boolean, default: false },
      numbered: { type: Boolean, default: false },
      black: { type: Boolean, default: false },
      yellow: { type: Boolean, default: false },
      tight: { type: Boolean, default: false },
      small: { type: Boolean, default: false }
    },
    data: function () { return { hoverRating: 0 }; },
    computed: {
      hasRating: function () { return this.rating !== '' || this.rating > 0; },
      stars: function () { return this.max; },
      percentageRating: function () {
        var r = this.interactive && this.hoverRating > 0 ? this.hoverRating : this.rating;
        var pct = (parseFloat(r) * 100 / this.max);
        var rem = r % 1.0;
        if (rem > 0 && rem <= 0.3) pct += 2 / this.max * 5;
        else if (rem > 0 && rem <= 0.375) pct += 1.5 / this.max * 5;
        else if (rem > 0 && rem <= 0.475) pct += 0.5 / this.max * 5;
        return Math.max(0, Math.min(100, pct)).toFixed(1);
      }
    },
    methods: {
      onRatingInput: function (r) { this.hoverRating = 0; this.$emit('input', r); }
    },
    template: '#vue-template-reviews-stars'
  };

  // ---------------------------------------------------------------------------
  // Info icon (bundle indicator)
  // ---------------------------------------------------------------------------
  var ProductReviewsInfo = {
    name: 'ProductReviewsInfo',
    template: '#vue-template-reviews-info'
  };

  // ---------------------------------------------------------------------------
  // Text components
  // ---------------------------------------------------------------------------
  var ProductReviewsTextTotal = {
    name: 'ProductReviewsTextTotal',
    props: {
      total: { type: Number, required: true },
      tag: { type: String, default: 'div' },
      unlabelled: { type: Boolean, default: false }
    },
    template: '#vue-template-reviews-text-total'
  };

  var ProductReviewsTextOutOf = {
    name: 'ProductReviewsTextOutOf',
    props: { count: { type: Number, required: true } },
    template: '#vue-template-reviews-text-out-of'
  };

  // ---------------------------------------------------------------------------
  // Collection product reviews (composable-powered)
  // ---------------------------------------------------------------------------
  var CollectionProductReviews = {
    name: 'CollectionProductReviews',
    components: { ProductReviewsStars: ProductReviewsStars, ProductReviewsTextTotal: ProductReviewsTextTotal, ProductReviewsTextOutOf: ProductReviewsTextOutOf, ProductReviewsInfo: ProductReviewsInfo },
    props: {
      handle: { type: String, required: true },
      showAverage: { type: Boolean, default: false }
    },
    setup: function (props) {
      if (!collectionRatings) return { shouldShow: Vue.ref(false) };
      return {
        ratingAvg: Vue.computed(function () { return collectionRatings.getRatingAvg(props.handle); }),
        ratingCount: Vue.computed(function () { return collectionRatings.getRatingCount(props.handle); }),
        isBundle: Vue.computed(function () { return collectionRatings.getIsBundle(props.handle); }),
        shouldShow: Vue.computed(function () { return collectionRatings.getRatingCount(props.handle) > 0; })
      };
    },
    template: '#vue-template-reviews-collection'
  };

  // ---------------------------------------------------------------------------
  // Product reviews top (star bar on product pages)
  // ---------------------------------------------------------------------------
  var ProductReviewsTop = {
    name: 'ProductReviewsTop',
    components: { ProductReviewsStars: ProductReviewsStars, ProductReviewsTextTotal: ProductReviewsTextTotal, ProductReviewsInfo: ProductReviewsInfo },
    props: {
      handle: { type: String, required: true },
      clickHandler: { type: String, default: null },
      infoClickHandler: { type: String, default: null }
    },
    setup: function (props) {
      var overview = getProductOverview(props.handle);
      Vue.watch(function () { return overview.ratingCount.value; }, function (count) {
        if (count > 0) {
          try { updateProductSchema({ ratingCount: count, ratingValue: overview.ratingAvg.value }); } catch (e) { console.error('[reviews-bridge] Schema update failed', e); }
        }
      });
      return {
        ratingAvg: overview.ratingAvg,
        ratingCount: overview.ratingCount,
        isBundle: overview.isBundle
      };
    },
    methods: {
      onClick: function () {
        if (this.clickHandler && typeof window[this.clickHandler] === 'function') window[this.clickHandler]();
      },
      onInfoClick: function () {
        if (this.infoClickHandler && typeof window[this.infoClickHandler] === 'function') window[this.infoClickHandler]();
      }
    },
    template: '#vue-template-reviews-top'
  };

  // ---------------------------------------------------------------------------
  // Reviews page total (aggregate stars for reviews page)
  // ---------------------------------------------------------------------------
  var ReviewsPageTotalProductReviews = {
    name: 'ReviewsPageTotalProductReviews',
    components: { ProductReviewsStars: ProductReviewsStars, ProductReviewsTextTotal: ProductReviewsTextTotal },
    props: { tag: { type: String, default: 'div' } },
    setup: function () {
      var ratingAvg = Vue.ref(0);
      var ratingCount = Vue.ref(0);
      function computeTotals () {
        var data = collectionRatings.overview.value;
        var totalCount = 0;
        var totalSum = 0;
        Object.keys(data).forEach(function (handle) {
          totalCount += data[handle].rating_count || 0;
          totalSum += (data[handle].rating_avg || 0) * (data[handle].rating_count || 0);
        });
        ratingCount.value = totalCount;
        ratingAvg.value = totalCount > 0 ? (totalSum / totalCount) : 0;
        if (totalCount > 0) {
          try { updateOrganizationSchema({ ratingCount: totalCount, ratingValue: ratingAvg.value }); } catch (e) { console.error('[reviews-bridge] Schema update failed', e); }
        }
      }
      if (collectionRatings.isLoaded.value) {
        computeTotals();
      } else {
        Vue.watch(function () { return collectionRatings.isLoaded.value; }, function (loaded) {
          if (loaded) computeTotals();
        });
      }
      return { ratingAvg: ratingAvg, ratingCount: ratingCount };
    },
    template: '#vue-template-reviews-page-total'
  };

  // ---------------------------------------------------------------------------
  // Reviews page product card (wraps collection-product-reviews)
  // ---------------------------------------------------------------------------
  var ReviewsPageProductReviews = {
    name: 'ReviewsPageProductReviews',
    components: { CollectionProductReviews: CollectionProductReviews },
    props: {
      handle: { type: String, required: true },
      showAverage: { type: Boolean, default: false }
    },
    data: function () { return { isMounted: false }; },
    computed: {
      shouldShowSlot: function () {
        if (!this.isMounted || !this.$refs.cpr) return false;
        return this.$refs.cpr.shouldShow;
      }
    },
    mounted: function () { this.isMounted = true; },
    template: '#vue-template-reviews-page-product'
  };

  // ---------------------------------------------------------------------------
  // Product reviews tab header (for tab-based product layouts)
  // ---------------------------------------------------------------------------
  var ProductReviewsTab = {
    name: 'ProductReviewsTab',
    components: { ProductReviewsStars: ProductReviewsStars, ProductReviewsTextTotal: ProductReviewsTextTotal },
    props: {
      handle: { type: String, required: true },
      clickHandler: { type: String, default: null }
    },
    data: function () {
      return { isViewLoaded: false };
    },
    setup: function (props) {
      var overview = getProductOverview(props.handle);
      var reviewList = SDK.useReviewList(client);
      return { ovSrc: overview, rlSrc: reviewList };
    },
    computed: {
      canWrite: function () { return (writeTargets[this.handle] || 0) > 0; },
      noReviewsYet: function () { return hasNoReviewsYet(this.ovSrc); },
      ratingAvg: function () { return this.ovSrc.ratingAvg.value; },
      ratingCount: function () { return this.ovSrc.ratingCount.value; },
      isLoading: function () { return this.ovSrc.isLoading.value || this.rlSrc.isLoading.value; },
      firstReview: function () {
        var r = this.rlSrc.reviews.value;
        return r.length > 0 ? r[0] : null;
      },
      shouldShow: function () {
        return this.ratingCount > 0 || this.isLoading;
      }
    },
    methods: {
      load: function () {
        var self = this;
        if (this.rlSrc.reviews.value.length > 0) {
          self.isViewLoaded = true;
          return Promise.resolve();
        }
        return this.rlSrc.load(this.handle).then(function () {
          self.isViewLoaded = true;
        });
      },
      onSeeAll: function () {
        if (this.clickHandler && typeof window[this.clickHandler] === 'function') window[this.clickHandler]();
      },
      // The write sheet belongs to the review section (ProductReviews); ask it to open.
      onWrite: function () {
        if (!this.canWrite) return;
        document.dispatchEvent(new CustomEvent(OPEN_WRITE_EVENT, { detail: { handle: this.handle } }));
      }
    },
    mounted: function () {
      var self = this;
      if (typeof IntersectionObserver !== 'undefined') {
        self.tabObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && !self.isViewLoaded) {
              self.load();
              self.tabObserver.disconnect();
            }
          });
        }, { rootMargin: '300px' });
        self.tabObserver.observe(self.$el);
      } else {
        setTimeout(function () { self.load(); }, 1000);
      }
    },
    beforeDestroy: function () {
      if (this.tabObserver) this.tabObserver.disconnect();
    },
    template: '#vue-template-reviews-tab'
  };

  // ---------------------------------------------------------------------------
  // Product reviews listing (full review section on product pages)
  // ---------------------------------------------------------------------------
  var ProductReviews = {
    name: 'ProductReviews',
    components: { ProductReviewsStars: ProductReviewsStars, ProductReviewsTextTotal: ProductReviewsTextTotal },
    props: {
      handle: { type: String, required: true },
      customerId: { type: String, default: null },
      customerName: { type: String, default: null },
      customerEmail: { type: String, default: null },
      writeEvent: { type: String, default: null },
      reportEvent: { type: String, default: null }
    },
    data: function () {
      return {
        isViewLoaded: false,
        showWrite: false,
        wClosing: false,
        showTitle: false,
        showBody: true,
        wStep: 1,
        wAnswers: {},
        wHeightValues: {},
        wPhotos: [],
        wPhotoPreviewUrls: [],
        wPhotoError: null,
        activeSort: null,
        activeRate: null,
        loadedReviews: [],
        loadedPage: 0,
        isLoadingMore: false,
        wSubmitted: null,
        wErrorKey: 'errors.validation',
        rErrorKey: 'errors.validation',
        reportItem: null,
        rClosing: false,
        upvotedIds: {},
        feedbackId: null,
        feedbackTimeout: null
      };
    },
    setup: function (props) {
      var overview = getProductOverview(props.handle);
      var reviewList = getProductReviewList(props.handle);
      var upvoteHelper = SDK.useUpvote(client, { handle: props.handle });
      var originalHelper = SDK.useOriginalReview(client);

      var customer = {};
      if (props.customerId) customer.id = props.customerId;
      if (props.customerName) customer.name = decodeURIComponent(props.customerName);
      if (props.customerEmail) customer.email = decodeURIComponent(props.customerEmail);

      var writeForm = SDK.useReviewForm(client, { handle: props.handle, customer: customer });
      var reportForm = SDK.useReportForm(client, { handle: props.handle, customer: customer });

      return {
        ovSrc: overview,
        rlSrc: reviewList,
        uvSrc: upvoteHelper,
        origSrc: originalHelper,
        wfSrc: writeForm,
        rfSrc: reportForm
      };
    },
    computed: {
      ratingAvg: function () { return this.ovSrc.ratingAvg.value; },
      ratingCount: function () { return this.ovSrc.ratingCount.value; },
      isOverviewLoaded: function () { return this.ovSrc.isLoaded.value; },
      subtitle: function () { return this.ovSrc.subtitle.value; },
      customQuestions: function () { return this.ovSrc.customQuestions ? this.ovSrc.customQuestions.value || [] : []; },
      ratingStatistic: function () { return this.ovSrc.ratingStatistic ? this.ovSrc.ratingStatistic.value || [] : []; },
      availableSorts: function () { return this.ovSrc.availableSorts ? this.ovSrc.availableSorts.value || [] : []; },
      starHistogramEnabled: function () { return this.ovSrc.starHistogramEnabled ? this.ovSrc.starHistogramEnabled.value !== false : true; },
      // SDK field `summary` (API `summary`): overall_summary plus topics[] with label, sentiment, mention_count.
      aiSummary: function () { return this.ovSrc.summary ? this.ovSrc.summary.value : null; },
      defaultSort: function () { return this.ovSrc.defaultSort ? this.ovSrc.defaultSort.value || null : null; },
      currentSort: function () { return this.activeSort || this.defaultSort; },
      reviews: function () { return this.rlSrc.reviews.value; },
      currentPage: function () { return this.rlSrc.currentPage.value; },
      lastPage: function () { return this.rlSrc.lastPage.value; },
      hasMoreReviews: function () { return this.loadedReviews.length > 0 && this.loadedPage < this.lastPage; },
      // Total for the "viewing x-y of z" line: the histogram count of the active
      // star filter, otherwise all reviews.
      viewingTotal: function () {
        var rate = this.activeRate;
        if (!rate) return this.ratingCount;
        var stat = this.ratingStatistic.filter(function (s) { return s.rate === rate; })[0];
        return stat ? stat.count : this.loadedReviews.length;
      },
      isListLoading: function () { return this.rlSrc.isLoading.value; },
      shouldShow: function () {
        return this.ratingAvg > 0 || this.ratingCount > 0 || this.isListLoading || !this.isViewLoaded || !this.isOverviewLoaded;
      },
      noReviewsYet: function () { return hasNoReviewsYet(this.ovSrc); },
      shouldShowEmpty: function () {
        return this.isViewLoaded && this.isOverviewLoaded && !this.ratingCount && !this.ratingAvg && !this.isListLoading;
      },
      enableInputs: function () { return this.wfSrc.rating.value > 0; },
      isMobile: function () { return window.innerWidth < 768; },
      wRating: {
        get: function () { return this.wfSrc.rating.value; },
        set: function (v) { this.wfSrc.setRating(v); }
      },
      wTitle: {
        get: function () { return this.wfSrc.title.value || ''; },
        set: function (v) { this.wfSrc.title.value = v; }
      },
      wBody: {
        get: function () { return this.wfSrc.body.value || ''; },
        set: function (v) { this.wfSrc.body.value = v; }
      },
      wName: {
        get: function () { return this.wfSrc.name.value || ''; },
        set: function (v) { this.wfSrc.name.value = v; }
      },
      wEmail: {
        get: function () { return this.wfSrc.email.value || ''; },
        set: function (v) { this.wfSrc.email.value = v; }
      },
      wOrderName: {
        get: function () { return this.wfSrc.orderName.value || ''; },
        set: function (v) { this.wfSrc.orderName.value = v; }
      },
      wCanSubmit: function () { return this.wfSrc.canSubmit.value; },
      wIsLoading: function () { return this.wfSrc.isLoading.value; },
      wIsSuccess: function () { return this.wfSrc.isSuccess.value; },
      wIsError: function () { return this.wfSrc.isError.value; },
      hasWizard: function () { return this.customQuestions.length > 0; },
      // The SDK field is a readonly ref: read .value (the bare ref is always truthy).
      photoUploadsEnabled: function () {
        return !!(this.ovSrc.photoUploadsEnabled && this.ovSrc.photoUploadsEnabled.value);
      },
      // Wizard: 1 rating and details, 2 questions, 3 photos (only when enabled).
      totalSteps: function () {
        if (!this.hasWizard) return 1;
        return this.photoUploadsEnabled ? 3 : 2;
      },
      questionsRequired: function () { return this.ovSrc.customQuestionsRequired ? this.ovSrc.customQuestionsRequired.value : false; },
      allQuestionsAnswered: function () {
        var self = this;
        return this.customQuestions.every(function (q) {
          if (q.input_type === 'height') return !!self.wHeightValues[q.id];
          return !!self.wAnswers[q.id];
        });
      },
      canGoNext: function () {
        if (this.wStep === 2 && this.questionsRequired) return this.allQuestionsAnswered;
        return true;
      },
      // Success screen: the SDK clears the form after a successful submit, so
      // rating and email are captured in submitReview. Email masked as e•••@domain.
      wSubmittedRating: function () { return this.wSubmitted ? this.wSubmitted.rating : 0; },
      wSubmittedEmail: function () {
        var email = (this.wSubmitted && this.wSubmitted.email) || '';
        var at = email.indexOf('@');
        if (at < 1) return email;
        return email.charAt(0) + '\u2022\u2022\u2022' + email.slice(at);
      },
      rReason: {
        get: function () { return this.rfSrc.reason.value || ''; },
        set: function (v) { this.rfSrc.reason.value = v; }
      },
      rBody: {
        get: function () { return this.rfSrc.body.value || ''; },
        set: function (v) { this.rfSrc.body.value = v; }
      },
      rEmail: {
        get: function () { return this.rfSrc.email.value || ''; },
        set: function (v) { this.rfSrc.email.value = v; }
      },
      rCanSubmit: function () { return this.rfSrc.canSubmit.value; },
      rIsLoading: function () { return this.rfSrc.isLoading.value; },
      rIsSuccess: function () { return this.rfSrc.isSuccess.value; },
      rIsError: function () { return this.rfSrc.isError.value; }
    },
    methods: {
      load: function () {
        var self = this;
        if (this.rlSrc.reviews.value.length > 0) {
          self.isViewLoaded = true;
          return Promise.resolve();
        }
        return this.rlSrc.load(this.handle).then(function () {
          self.isViewLoaded = true;
        });
      },
      // Load more like the PLP. The API serves fixed pages of 3 with no page
      // size parameter, so the first view shows one page and each load more
      // fetches LOAD_MORE_PAGES pages. Sequential on purpose: the SDK cancels
      // a running list request when the next one for the same product starts.
      loadMore: function () {
        var self = this;
        var LOAD_MORE_PAGES = 2;
        if (!this.hasMoreReviews || this.isLoadingMore) return;
        this.isLoadingMore = true;
        function next (remaining) {
          var before = self.loadedPage;
          if (remaining <= 0 || !self.hasMoreReviews) return Promise.resolve();
          return self.rlSrc.goToPage(self.handle, before + 1).then(function () {
            // Append now instead of waiting for the watcher (next tick) so the
            // following request asks for the right page.
            self.syncLoadedReviews();
            // A failed page leaves loadedPage unchanged: stop, the button stays for a retry.
            if (self.loadedPage === before) return;
            return next(remaining - 1);
          });
        }
        next(LOAD_MORE_PAGES).then(function () {
          self.isLoadingMore = false;
        }, function () {
          self.isLoadingMore = false;
        });
      },
      // The SDK list holds one page at a time. Page 1 (initial load, new sort or
      // star filter) starts over; a later page is appended, skipping duplicates
      // in case reviews shifted between requests.
      syncLoadedReviews: function () {
        var page = this.currentPage;
        var list = this.reviews || [];
        if (page <= 1 || !this.loadedPage) {
          this.loadedReviews = list.slice();
          this.loadedPage = list.length ? page : 0;
        } else if (page > this.loadedPage && list.length) {
          var seen = {};
          this.loadedReviews.forEach(function (r) { seen[r.id] = true; });
          this.loadedReviews = this.loadedReviews.concat(list.filter(function (r) { return !seen[r.id]; }));
          this.loadedPage = page;
        }
        // A failed later page comes back empty: keep what is loaded so
        // "load more" can be retried.
      },
      onSort: function (sort) {
        this.activeSort = sort;
        this.rlSrc.setSort(this.handle, sort);
      },
      onRateFilter: function (rate) {
        this.activeRate = this.activeRate === rate ? null : rate;
        this.rlSrc.setRate(this.handle, this.activeRate);
      },
      onUpvote: function (item) {
        if (this.upvotedIds[item.id]) return;
        var self = this;
        this.uvSrc.upvote(item.upvoteUrl).then(function () {
          self.$set(self.upvotedIds, item.id, true);
          self.feedbackId = item.id;
          if (self.feedbackTimeout) clearTimeout(self.feedbackTimeout);
          self.feedbackTimeout = setTimeout(function () { self.feedbackId = null; }, 5000);
        });
      },
      getDisplay: function (item) {
        return this.origSrc.get(item.id) || item;
      },
      hasOriginal: function (itemId) {
        return !!this.origSrc.get(itemId);
      },
      onOriginal: function (item) {
        this.origSrc.load(item.id, item.originalUrl);
      },
      onTranslation: function (item) {
        this.origSrc.clear(item.id);
      },
      // Report sheet: one per review list, filled with the reported review.
      openReport: function (item) {
        if (this.reportItem) return;
        this._reportOpener = document.activeElement;
        this.rfSrc.reset();
        this.reportItem = item;
        showSheet(this, 'reportSheet');
      },
      closeReport: function () {
        hideSheet(this, 'reportSheet', 'rClosing', this.onReportSheetClosed);
      },
      onReportSheetClick: function (event) {
        if (event.target === this.$refs.reportSheet) this.closeReport();
      },
      onReportSheetClosed: function () {
        var opener = this._reportOpener;
        document.documentElement.classList.remove('product-reviews-sheet-open');
        if (!this.reportItem) return;
        this._reportOpener = null;
        this.reportItem = null;
        this.rfSrc.reset();
        restoreFocus(opener);
      },
      // Success shows in the sheet until it is closed (no auto close). Errors
      // surface through rIsError, so the rejection is handled here.
      submitReport: function () {
        if (!this.reportItem) return;
        var self = this;
        this.rfSrc.submit(this.reportItem.reportUrl).catch(function (err) {
          self.rErrorKey = errorKey(err);
        });
      },
      heightUnit: function () {
        return heightUnitSystem;
      },
      heightCm: function (qId) {
        return this.wHeightValues[qId] || '';
      },
      heightFt: function (qId) {
        return this.wHeightValues[qId + '_ft'] || '';
      },
      heightIn: function (qId) {
        return this.wHeightValues[qId + '_in'] || '';
      },
      setHeightCm: function (qId, val) {
        var num = parseInt(val) || 0;
        this.$set(this.wHeightValues, qId, num);
        var totalInches = Math.round(num / 2.54);
        this.$set(this.wHeightValues, qId + '_ft', Math.floor(totalInches / 12));
        this.$set(this.wHeightValues, qId + '_in', totalInches % 12);
      },
      setHeightImperial: function (qId, feet, inches) {
        var ft = parseInt(feet) || 0;
        var inc = parseInt(inches) || 0;
        this.$set(this.wHeightValues, qId + '_ft', ft);
        this.$set(this.wHeightValues, qId + '_in', inc);
        this.$set(this.wHeightValues, qId, Math.round((ft * 12 + inc) * 2.54));
      },
      onPhotoChange: function (event) {
        var self = this;
        var files = Array.from(event.target.files || []);
        var maxFiles = 5;
        var maxSize = 10 * 1024 * 1024;
        self.wPhotoError = null;
        var current = self.wPhotos.length;
        var allowed = files.slice(0, maxFiles - current);
        if (files.length > allowed.length) {
          self.wPhotoError = 'Maximum ' + maxFiles + ' photos allowed.';
        }
        for (var i = 0; i < allowed.length; i++) {
          if (allowed[i].size > maxSize) {
            self.wPhotoError = 'Each photo must be under 10 MB.';
            continue;
          }
          self.wPhotos.push(allowed[i]);
          self.wPhotoPreviewUrls.push(URL.createObjectURL(allowed[i]));
        }
        if (event.target) event.target.value = '';
      },
      removePhoto: function (index) {
        URL.revokeObjectURL(this.wPhotoPreviewUrls[index]);
        this.wPhotos.splice(index, 1);
        this.wPhotoPreviewUrls.splice(index, 1);
        this.wPhotoError = null;
      },
      nextStep: function () {
        if (this.wStep < this.totalSteps) this.wStep++;
      },
      prevStep: function () {
        if (this.wStep > 1) this.wStep--;
      },
      submitReview: function () {
        var self = this;
        var answers = [];
        var questions = this.customQuestions;
        var questionMap = {};
        questions.forEach(function (q) { questionMap[q.id] = q; });
        Object.keys(this.wAnswers).forEach(function (qId) {
          var optId = self.wAnswers[qId];
          if (optId) answers.push({ question_id: parseInt(qId), option_id: parseInt(optId) });
        });
        Object.keys(this.wHeightValues).forEach(function (key) {
          if (key.indexOf('_') !== -1) return;
          var qId = parseInt(key);
          var question = questionMap[qId];
          if (question && question.input_type === 'height' && self.wHeightValues[key]) {
            answers.push({ question_id: qId, value: String(self.wHeightValues[key]) });
          }
        });
        this.wSubmitted = { rating: this.wRating, email: this.wEmail };
        this.wfSrc.submit({ answers: answers, photos: this.wPhotos }).then(function () {
          if (self.writeEvent) {
            document.dispatchEvent(new CustomEvent(self.writeEvent));
          }
        }, function (err) {
          self.wErrorKey = errorKey(err);
        });
      },
      // Write sheet: open/close mirror the size sheet in wb-card-swatches.js.
      openWrite: function () {
        if (this.showWrite) return;
        this._writeOpener = document.activeElement;
        this.showWrite = true;
        showSheet(this, 'writeSheet');
      },
      closeWrite: function () {
        hideSheet(this, 'writeSheet', 'wClosing', this.onWriteSheetClosed);
      },
      // The dialog element itself is only hit on the backdrop.
      onWriteSheetClick: function (event) {
        if (event.target === this.$refs.writeSheet) this.closeWrite();
      },
      // Runs for both closeWrite and the native close event, so it must be idempotent.
      onWriteSheetClosed: function () {
        var opener = this._writeOpener;
        document.documentElement.classList.remove('product-reviews-sheet-open');
        if (!this.showWrite) return;
        this._writeOpener = null;
        this.resetWrite();
        restoreFocus(opener);
      },
      resetWrite: function () {
        this.wfSrc.reset();
        this.showWrite = false;
        this.wSubmitted = null;
        this.showTitle = false;
        this.showBody = true;
        this.wStep = 1;
        this.wAnswers = {};
        this.wHeightValues = {};
        this.wPhotoPreviewUrls.forEach(function (url) { URL.revokeObjectURL(url); });
        this.wPhotos = [];
        this.wPhotoPreviewUrls = [];
        this.wPhotoError = null;
      }
    },
    watch: {
      reviews: function () { this.syncLoadedReviews(); },
      wRating: function (newVal, oldVal) {
        var oldDefault = oldVal > 0 ? this.$t('avgOutOf', { avg: this.$n(oldVal), total: this.$n(5) }) : null;
        var newDefault = newVal > 0 ? this.$t('avgOutOf', { avg: this.$n(newVal), total: this.$n(5) }) : null;
        if (!this.wTitle || this.wTitle === oldDefault) this.wTitle = newDefault;
        if (!this.wBody || this.wBody === oldDefault) this.wBody = newDefault;
      },
      isOverviewLoaded: function (loaded) {
        if (!loaded) return;
        if (this.ratingCount > 0 && this._pendingLoad) {
          this._pendingLoad = false;
          this.load();
        } else if (!this.ratingCount) {
          this.isViewLoaded = true;
        }
      }
    },
    mounted: function () {
      var self = this;
      self._pendingLoad = false;
      self.syncLoadedReviews();
      self._onOpenWrite = function (event) {
        if (!event.detail || event.detail.handle === self.handle) self.openWrite();
      };
      document.addEventListener(OPEN_WRITE_EVENT, self._onOpenWrite);
      Vue.set(writeTargets, self.handle, (writeTargets[self.handle] || 0) + 1);
      var triggerLoad = function () {
        if (self.isOverviewLoaded && self.ratingCount > 0) {
          self.load();
        } else {
          self._pendingLoad = true;
        }
      };
      if (typeof IntersectionObserver !== 'undefined') {
        self._observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && !self.isViewLoaded) {
              triggerLoad();
              self._observer.disconnect();
            }
          });
        }, { rootMargin: '300px' });
        self._observer.observe(self.$el);
      } else {
        setTimeout(function () { triggerLoad(); }, 1000);
      }
    },
    beforeDestroy: function () {
      if (this._observer) this._observer.disconnect();
      if (this.feedbackTimeout) clearTimeout(this.feedbackTimeout);
      if (this.showWrite || this.reportItem) document.documentElement.classList.remove('product-reviews-sheet-open');
      if (this._onOpenWrite) document.removeEventListener(OPEN_WRITE_EVENT, this._onOpenWrite);
      Vue.set(writeTargets, this.handle, Math.max((writeTargets[this.handle] || 1) - 1, 0));
    },
    template: '#vue-template-reviews-product'
  };

  // ---------------------------------------------------------------------------
  // Member area review write (line-item review from customer account)
  // ---------------------------------------------------------------------------
  var MemberAreaReviewWrite = {
    name: 'MemberAreaReviewWrite',
    components: { ProductReviewsStars: ProductReviewsStars },
    props: {
      orderId: { type: String, required: true },
      lineItemId: { type: String, required: true },
      sku: { type: String, default: '' },
      lineItemTitle: { type: String, default: '' },
      submitEvent: { type: String, default: null },
      contentHtml: { type: String, default: '' },
      customerName: { type: String, default: null },
      customerEmail: { type: String, default: null },
      customerId: { type: String, default: null },
      orderName: { type: String, default: null }
    },
    setup: function (props) {
      var customer = {};
      if (props.customerId) customer.id = props.customerId;
      if (props.customerName) customer.name = props.customerName;
      if (props.customerEmail) customer.email = props.customerEmail;

      var review = SDK.useLineItemReview(client, {
        orderId: props.orderId,
        lineItemId: props.lineItemId,
        sku: props.sku,
        customer: customer
      });
      return { src: review };
    },
    data: function () {
      return { submitErrorKey: 'errors.validation' };
    },
    computed: {
      rating: {
        get: function () { return this.src.rating.value; },
        set: function (v) { this.src.setRating(v); }
      },
      title: {
        get: function () { return this.src.title.value || ''; },
        set: function (v) { this.src.title.value = v; }
      },
      body: {
        get: function () { return this.src.body.value || ''; },
        set: function (v) { this.src.body.value = v; }
      },
      canSubmit: function () { return this.src.canSubmit.value; },
      isLoading: function () { return this.src.isLoading.value; },
      isSuccess: function () { return this.src.isSuccess.value; },
      isError: function () { return this.src.isError.value; },
      errors: function () { return this.src.errors.value; }
    },
    methods: {
      submit: function () {
        var self = this;
        var submittedRating = this.src.rating.value;
        // The SDK clears the form (orderName included) after each submit.
        if (this.orderName) this.src.orderName.value = this.orderName;
        this.src.submit().then(function () {
          if (self.submitEvent) {
            document.dispatchEvent(new CustomEvent(self.submitEvent, {
              detail: {
                orderId: self.orderId,
                lineItemId: self.lineItemId,
                sku: self.sku,
                rating: submittedRating
              }
            }));
          }
        }, function (err) {
          self.submitErrorKey = errorKey(err);
        });
      }
    },
    template: '#vue-template-member-area-review-write'
  };

  // ---------------------------------------------------------------------------
  // Sheets (write review, report review): native <dialog> opened with
  // showModal(), slide out via .is-closing before close. Mirrors the size
  // sheet in wb-card-swatches.js.
  // ---------------------------------------------------------------------------
  // The SDK rejects a failed submit with { message: <i18n key> }: errors.validation
  // (422), errors.rateLimit (429), errors.down (503) or errors.critical (anything
  // else, incl. timeouts). Anything unexpected falls back to errors.critical.
  function errorKey (err) {
    var key = err && err.message;
    return /^errors\.(validation|rateLimit|down|critical)$/.test(key) ? key : 'errors.critical';
  }

  // Fired by the reviews tab's empty state; the review section for that
  // handle opens its write sheet.
  var OPEN_WRITE_EVENT = 'wb:productreviews:openwrite';
  // Review sections on the page by handle (count). The tab only offers
  // "write a review" when one can open the sheet; templates without the
  // section (product.app-content, product.pdp-switch) get no button.
  var writeTargets = Vue.observable({});

  function showSheet (vm, refName) {
    vm.$nextTick(function () {
      var sheet = vm.$refs[refName];
      if (!sheet) return;
      document.documentElement.classList.add('product-reviews-sheet-open');
      if (typeof sheet.showModal === 'function') {
        sheet.showModal();
      } else {
        sheet.setAttribute('open', '');
      }
    });
  }

  // closingKey: the vm flag bound to .is-closing. onClosed must be idempotent:
  // it runs here and again from the dialog's native close event.
  function hideSheet (vm, refName, closingKey, onClosed) {
    var sheet = vm.$refs[refName];
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var done = false;
    var timer;
    if (!sheet || vm[closingKey]) return;
    function finish () {
      if (done) return;
      done = true;
      clearTimeout(timer);
      vm[closingKey] = false;
      if (sheet.open && typeof sheet.close === 'function') {
        sheet.close();
      }
      onClosed.call(vm);
    }
    if (reduced) {
      finish();
      return;
    }
    vm[closingKey] = true;
    sheet.addEventListener('animationend', function onEnd (event) {
      if (event.target === sheet) {
        sheet.removeEventListener('animationend', onEnd);
        finish();
      }
    });
    timer = setTimeout(finish, 400);
  }

  function restoreFocus (opener) {
    if (opener && typeof opener.focus === 'function' && document.body.contains(opener)) {
      opener.focus({ preventScroll: true });
    }
  }

  // ---------------------------------------------------------------------------
  // Mount helper (mirrors the old createApp pattern)
  // ---------------------------------------------------------------------------
  var SKIP_ATTRS = { class: 1, style: 1, id: 1, 'data-headless-init': 1, 'data-initialized': 1 };
  var mountedInstances = [];

  function teardownRemovedInstances () {
    mountedInstances = mountedInstances.filter(function (vm) {
      if (!document.body.contains(vm.$el)) {
        vm.$destroy();
        return false;
      }
      return true;
    });
  }

  // keepHostClass: new Vue({ el }) replaces the host element with the
  // component root, dropping the host's classes. Collection cards style the
  // host (e.g. wb-prdct20-fitness__reviews-container margin-top), so their
  // classes are carried over to the root as the old bundle's markup had them.
  // Content inside the host element becomes the component's default slot, as
  // the old bundle's in-DOM templates had it (reviews page: the heading text
  // next to the total, the "check reviews" button on each product card).
  // Plain text stays text (with a leading space); markup keeps a wrapper.
  function hostSlot (el, h) {
    var html = el.innerHTML.trim();
    if (!html) return undefined;
    if (!el.children.length) return [' ' + el.textContent.trim()];
    return [h('div', { class: 'headless-host-slot', domProps: { innerHTML: html } })];
  }

  function mountComponents (selector, component, keepHostClass) {
    teardownRemovedInstances();
    var els = document.querySelectorAll(selector);
    return Array.from(els)
      .filter(function (el) { return el.dataset.headlessInit !== 'true'; })
      .map(function (el) {
        el.dataset.headlessInit = 'true';
        var props = {};
        Array.from(el.attributes).forEach(function (attr) {
          var name = attr.name;
          if (SKIP_ATTRS[name]) return;
          if (name.indexOf('data-') === 0) name = name.slice(5);
          var camel = name.replace(/-([a-z])/g, function (_, c) { return c.toUpperCase(); });
          var val = attr.value;
          if (val === 'true') val = true;
          else if (val === 'false') val = false;
          props[camel] = val;
        });
        var hostClass = keepHostClass ? el.getAttribute('class') : null;
        var slotHtml = el.innerHTML;
        var slotEl = document.createElement('div');
        slotEl.innerHTML = slotHtml;
        var vm = new Vue({
          el: el,
          i18n: i18nInstance,
          render: function (h) {
            return h(component, hostClass ? { props: props, class: hostClass } : { props: props }, hostSlot(slotEl, h));
          }
        });
        if (vm.$el && vm.$el.dataset) vm.$el.dataset.headlessInit = 'true';
        mountedInstances.push(vm);
        return vm;
      });
  }

  // ---------------------------------------------------------------------------
  // Public facade — backward-compatible with window.productReviews()
  // ---------------------------------------------------------------------------
  var productReviews = function (options) {
    var shop = options.shop;
    var uuid = options.uuid;
    var baseUrl = options.baseUrl || '/';

    apiBaseUrl = baseUrl.replace(/\/$/, '') + '/';
    heightUnitSystem = options.heightUnit === 'ft' ? 'ft' : 'cm';
    // language: the theme's currentLang ('en' | 'de' | 'fr' | 'es'), so the EU
    // shop can serve Spanish. SDKs before the Spanish release ignore it.
    var clientOptions = { shop: shop, uuid: uuid, baseUrl: baseUrl };
    if (options.language) clientOptions.language = options.language;
    client = SDK.createReviewsClient(clientOptions);
    collectionRatings = SDK.useCollectionRatings(client);
    ensureI18n(client.locale);

    var topSelector = options.topSelector || '.app-productreviewstop';
    var tabSelector = options.tabSelector || '.app-productreviewstab';
    var reviewsSelector = options.reviewsSelector || '.app-productreviews';
    var collectionSelector = options.collectionSelector || '.app-collectionproductreviews';
    var totalSelector = options.totalSelector || '.app-totalproductreviews';
    var reviewPageCollectionSelector = options.reviewPageCollectionSelector || '.app-reviewcollectionproductreviews';

    var updateProductPage = function () {
      mountComponents(reviewsSelector, ProductReviews);
      return loadCollectionRatings().then(function () {
        mountComponents(topSelector, ProductReviewsTop);
        mountComponents(tabSelector, ProductReviewsTab);
      });
    };

    var updateProductPageReviews = function () {
      mountComponents(reviewsSelector, ProductReviews);
      return Promise.resolve();
    };

    var showProductPageReview = function () {
      return Promise.resolve();
    };

    var updateCollection = function () {
      return loadCollectionRatings().then(function () {
        mountComponents(collectionSelector, CollectionProductReviews, true);
      });
    };

    var updateReviewsPage = function () {
      return loadCollectionRatings().then(function () {
        mountComponents(totalSelector, ReviewsPageTotalProductReviews);
        mountComponents(reviewPageCollectionSelector, ReviewsPageProductReviews);
      });
    };

    var updateMemberArea = function (memberOptions) {
      memberOptions = memberOptions || {};
      var customer = memberOptions.customer || {};
      teardownRemovedInstances();

      var els = document.querySelectorAll('.app-memberareareviews-write');
      Array.from(els)
        .filter(function (el) { return el.dataset.headlessInit !== 'true'; })
        .forEach(function (el) {
          el.dataset.headlessInit = 'true';
          var contentHtml = el.innerHTML;
          var props = {};
          Array.from(el.attributes).forEach(function (attr) {
            var name = attr.name;
            if (SKIP_ATTRS[name]) return;
            if (name.indexOf('data-') === 0) name = name.slice(5);
            var camel = name.replace(/-([a-z])/g, function (_, c) { return c.toUpperCase(); });
            var val = attr.value;
            if (val === 'true') val = true;
            else if (val === 'false') val = false;
            props[camel] = val;
          });
          props.contentHtml = contentHtml;
          if (customer.id) props.customerId = String(customer.id);
          if (customer.name) props.customerName = customer.name;
          if (customer.email) props.customerEmail = customer.email;

          var vm = new Vue({
            el: el,
            i18n: i18nInstance,
            render: function (h) {
              return h(MemberAreaReviewWrite, { props: props });
            }
          });
          if (vm.$el && vm.$el.dataset) vm.$el.dataset.headlessInit = 'true';
          mountedInstances.push(vm);
        });

      return Promise.resolve();
    };

    return {
      updateProductPage: updateProductPage,
      updateProductPageReviews: updateProductPageReviews,
      showProductPageReview: showProductPageReview,
      updateCollection: updateCollection,
      updateReviewsPage: updateReviewsPage,
      updateMemberArea: updateMemberArea,
      client: client,
      collectionRatings: collectionRatings
    };
  };

  // ---------------------------------------------------------------------------
  // Post-purchase review (tokenized review page)
  // ---------------------------------------------------------------------------
  function initPostPurchaseReview (el, options) {
    options = options || {};
    var mountEl = typeof el === 'string' ? document.querySelector(el) : el;
    if (!mountEl || !client) return null;

    var params = new URLSearchParams(window.location.search);
    var orderId = options.orderId || params.get('order');
    var token = options.token || params.get('token');
    var encodedEmail = options.email || params.get('email');

    if (!orderId || !token || !encodedEmail) {
      console.warn('[reviews-bridge] Missing order, token, or email params');
      return null;
    }

    var i18n = ensureI18n(client.locale);

    return new Vue({
      el: mountEl,
      i18n: i18n,
      components: { ProductReviewsStars: ProductReviewsStars },
      data: function () {
        return {
          orderId: orderId,
          orderData: null,
          review: null,
          localItems: [],
          localShippingRating: 0,
          localShippingComment: '',
          localOverallRating: 0,
          localOverallComment: '',
          localNpsScore: null,
          localIsLoading: false,
          localIsSuccess: false,
          localIsError: false,
          lowRating: false,
          isLoadingOrder: true,
          orderError: null
        };
      },
      computed: {
        items: function () {
          var sdkItems = this.review && this.review.items ? this.review.items.value : null;
          return sdkItems && sdkItems.length ? sdkItems : this.localItems;
        },
        shippingRating: {
          get: function () { return this.review && this.review.shippingRating ? this.review.shippingRating.value : this.localShippingRating; },
          set: function (v) { if (this.review && this.review.shippingRating) { this.review.shippingRating.value = v; } this.localShippingRating = v; }
        },
        shippingComment: {
          get: function () { return this.review && this.review.shippingComment ? this.review.shippingComment.value : this.localShippingComment; },
          set: function (v) { if (this.review && this.review.shippingComment) { this.review.shippingComment.value = v; } this.localShippingComment = v; }
        },
        overallRating: {
          get: function () { return this.review && this.review.overallRating ? this.review.overallRating.value : this.localOverallRating; },
          set: function (v) { if (this.review && this.review.overallRating) { this.review.overallRating.value = v; } this.localOverallRating = v; }
        },
        overallComment: {
          get: function () { return this.review && this.review.overallComment ? this.review.overallComment.value : this.localOverallComment; },
          set: function (v) { if (this.review && this.review.overallComment) { this.review.overallComment.value = v; } this.localOverallComment = v; }
        },
        npsScore: {
          get: function () { return this.review && this.review.npsScore ? this.review.npsScore.value : this.localNpsScore; },
          set: function (v) { if (this.review && this.review.npsScore) { this.review.npsScore.value = v; } this.localNpsScore = v; }
        },
        canSubmit: function () {
          return this.items.length > 0 && this.items.every(function (i) { return i.rating > 0; });
        },
        hasSubmitted: function () { return this.localIsSuccess; },
        isLoading: function () { return this.localIsLoading; },
        isSuccess: function () { return this.localIsSuccess; },
        isError: function () { return this.localIsError; },
        errors: function () { return this.review && this.review.errors ? this.review.errors.value : {}; },
        shippingCarrier: function () { return this.orderData ? this.orderData.shipping_carrier : null; },
        // The server decides after submit (low_rating in the response, across
        // product, shipping and overall ratings); the threshold is not public.
        hasLowRating: function () { return this.lowRating; }
      },
      methods: {
        setItemRating: function (lineItemId, rating) {
          if (this.review && typeof this.review.setItemRating === 'function') {
            this.review.setItemRating(lineItemId, rating);
          }
          var local = this.localItems.find(function (i) { return i.lineItemId === lineItemId; });
          if (local) local.rating = rating;
        },
        setItemComment: function (lineItemId, comment) {
          if (this.review && typeof this.review.setItemComment === 'function') {
            this.review.setItemComment(lineItemId, comment);
          }
          var local = this.localItems.find(function (i) { return i.lineItemId === lineItemId; });
          if (local) local.comment = comment;
        },
        setNps: function (score) {
          if (this.review && this.review.npsScore) this.review.npsScore.value = score;
          this.localNpsScore = score;
        },
        submit: function () {
          var self = this;
          var customer = (this.orderData && this.orderData.customer) || {};
          var data = {
            order_type: 'post_purchase',
            order_name: (this.orderData && this.orderData.order_name) || null,
            shipping_carrier: this.shippingCarrier || null,
            name: customer.name || null,
            email: customer.email || null,
            customer_id: customer.id || null,
            nps_score: this.npsScore,
            line_items: this.items.map(function (i) {
              return {
                id: i.lineItemId,
                sku: i.sku,
                title: i.title,
                review: { rate: i.rating, title: null, body: i.comment || null }
              };
            }),
            other_items: [
              { id: 'shipping', sku: 'shipping', title: 'Shipping', review: { rate: self.shippingRating || 0, title: null, body: self.shippingComment || null } },
              { id: 'overall', sku: 'overall', title: 'Overall', review: { rate: self.overallRating || 0, title: null, body: self.overallComment || null } }
            ]
          };
          self.localIsLoading = true;
          self.localIsError = false;
          axios.post(apiBaseUrl + 'orders/' + encodeURIComponent(this.orderId) + '/reviews.json', data, {
            params: { token: token, email: encodedEmail }
          }).then(function (response) {
            self.lowRating = !!(response && response.data && response.data.low_rating === true);
            self.localIsLoading = false;
            self.localIsSuccess = true;
            document.dispatchEvent(new CustomEvent('wb:postpurchasereviewtracking', {
              detail: { orderId: self.orderId, success: true }
            }));
          }).catch(function () {
            self.localIsLoading = false;
            self.localIsError = true;
          });
        }
      },
      created: function () {
        var self = this;
        client.api.getOrderReviewData(orderId, token, encodedEmail)
          .then(function (data) {
            self.orderData = data;

            var customer = data.customer || {};
            self.review = SDK.useOrderReview(client, {
              orderId: data.order_id || orderId,
              orderName: data.order_name || null,
              shippingCarrier: data.shipping_carrier || null,
              customer: {
                id: customer.id,
                name: customer.name,
                email: customer.email
              },
              auth: { token: token, email: encodedEmail }
            });

            var mappedItems = (data.line_items || []).map(function (item) {
              return {
                lineItemId: item.line_item_id,
                sku: item.sku,
                title: item.title,
                imageUrl: item.image_url,
                variant: item.variant_title,
                rating: 0,
                comment: ''
              };
            });

            if (self.review && typeof self.review.addItem === 'function') {
              mappedItems.forEach(function (item) {
                try { self.review.addItem(item); } catch (e) { /* SDK may not support addItem */ }
              });
            }

            if (!(self.review && self.review.items && self.review.items.value && self.review.items.value.length)) {
              self.localItems = mappedItems;
            }

            self.isLoadingOrder = false;
          }, function (err) {
            self.orderError = (err && err.error) || 'invalid_or_expired_token';
            self.isLoadingOrder = false;
          })
          .catch(function (err) {
            console.error('[reviews-bridge] Post-purchase review init error', err);
            self.orderError = 'unexpected_error';
            self.isLoadingOrder = false;
          });
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Review verification (storefront-based email verification)
  // ---------------------------------------------------------------------------
  function initReviewVerification (el, options) {
    options = options || {};
    var mountEl = typeof el === 'string' ? document.querySelector(el) : el;
    if (!mountEl) return null;

    var params = new URLSearchParams(window.location.search);
    var token = options.token || params.get('token');
    var email = options.email || params.get('email');

    var i18n = ensureI18n(client ? client.locale : 'en-GB');

    return new Vue({
      el: mountEl,
      i18n: i18n,
      components: { ProductReviewsStars: ProductReviewsStars },
      data: function () {
        return {
          isLoading: true,
          verified: false,
          published: false,
          review: null,
          product: null,
          error: null
        };
      },
      created: function () {
        if (!token || !email || !apiBaseUrl) {
          this.error = 'invalid_or_expired_token';
          this.isLoading = false;
          return;
        }
        var self = this;
        axios.post(apiBaseUrl + 'reviews/verify', { token: token, email: email })
          .then(function (res) {
            var data = res.data || res;
            self.verified = data.verified;
            self.published = data.published;
            self.review = data.review;
            self.product = data.product;
            self.isLoading = false;
          })
          .catch(function (err) {
            self.error = (err.response && err.response.data && err.response.data.error) || 'verification_failed';
            self.isLoading = false;
          });
      }
    });
  }

  window.productReviews = productReviews;
  window.initPostPurchaseReview = initPostPurchaseReview;
  window.initReviewVerification = initReviewVerification;
  window.productReviewsComponents = {
    CollectionProductReviews: CollectionProductReviews,
    ProductReviews: ProductReviews,
    ProductReviewsTop: ProductReviewsTop,
    ProductReviewsTab: ProductReviewsTab,
    ProductReviewsStars: ProductReviewsStars,
    ReviewsPageProductReviews: ReviewsPageProductReviews,
    ReviewsPageTotalProductReviews: ReviewsPageTotalProductReviews,
    MemberAreaReviewWrite: MemberAreaReviewWrite
  };
})();
